import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, Pill } from "lucide-react";
import { MED } from "@/constants/testIds";
import { RXNORM_SYSTEM, listMedications } from "@/lib/medicationApi";
import "@/styles/emr-catalog.css";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join("; ");
  return "Unable to load medication catalog.";
}

export default function MedicationsPage() {
  const [items, setItems] = useState([]);
  const [system, setSystem] = useState(RXNORM_SYSTEM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listMedications({ active: true });
        if (cancelled) return;
        setItems(data.items || []);
        setSystem(data.system || RXNORM_SYSTEM);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="emr-catalog" data-testid={MED.page}>
      <div className="emr-catalog__pagehead">
        <h1 className="emr-catalog__title" data-testid={MED.title}>
          Medication Catalog
        </h1>
      </div>

      <div className="emr-catalog__workspace">
        <div className="emr-catalog__side">
          <section className="card emr-catalog__section emr-catalog__purpose">
            <h2 className="emr-catalog__section-title">Purpose</h2>
            <p className="emr-catalog__copy">
              Development/demo medication master for future Encounter orders. Not a patient
              medication list and not a prescribing screen.
            </p>
          </section>

          <section
            data-testid={MED.banner}
            className="card emr-catalog__section emr-catalog__notice emr-catalog__section--notice"
          >
            <h2 className="emr-catalog__section-title">Reference data</h2>
            <p className="emr-catalog__copy">
              Small demo subset only. Each row is catalog/reference data ({system}). Not a
              FHIR MedicationRequest.
            </p>
          </section>
        </div>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">Medications</h2>
          {error ? (
            <div data-testid={MED.errorState} className="emr-catalog__error">
              <AlertCircle className="emr-catalog__error-icon" aria-hidden="true" />
              <p className="emr-catalog__error-copy">{error}</p>
              <Button
                variant="outline"
                className="btn emr-catalog__retry"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div data-testid={MED.loading} className="emr-catalog__status-msg">
              Loading medication catalog…
            </div>
          ) : !items.length ? (
            <div data-testid={MED.emptyState} className="emr-catalog__empty">
              <Pill className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No demo medications are seeded yet.</p>
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table data-testid={MED.table} className="striped highlight emr-catalog__table">
                <thead>
                  <tr>
                    <th>Medication</th>
                    <th>Generic Name</th>
                    <th>Strength</th>
                    <th>Form</th>
                    <th>Route</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id || row.catalogKey} data-testid={`${MED.row}-${row.id}`}>
                      <td className="emr-catalog__name">{row.medicationName}</td>
                      <td>{row.genericName}</td>
                      <td className="emr-catalog__muted">{row.strength || "—"}</td>
                      <td className="emr-catalog__muted">{row.dosageForm || "—"}</td>
                      <td className="emr-catalog__muted">{row.route || "—"}</td>
                      <td>
                        {row.active !== false ? (
                          <span className="emr-catalog__status emr-catalog__status--active">Active</span>
                        ) : (
                          <span className="emr-catalog__status emr-catalog__status--inactive">Inactive</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
